import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const PhysicianAssistantCoursePage: React.FC = () => {
  const physicianAssistantData = {
    title: "Bachelor of Physician Assistant",
    shortName: "BPA",
    duration: "3 Years",
    description: "A specialized healthcare program training students to work as physician assistants, providing medical care under physician supervision.",
    overview: "Bachelor of Physician Assistant is an emerging healthcare program that prepares students to work as mid-level healthcare providers. Physician Assistants are trained to conduct physical examinations, diagnose illnesses, develop treatment plans, prescribe medications, and assist in surgeries under the supervision of licensed physicians. The program combines medical knowledge with practical clinical skills to address the growing demand for healthcare professionals.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Medical fitness certificate required",
      "Good communication and interpersonal skills",
      "Entrance examination (varies by institution)"
    ],
    careerProspects: {
      roles: [
        "Physician Assistant",
        "Medical Assistant",
        "Healthcare Coordinator",
        "Clinical Support Specialist",
        "Medical Scribe",
        "Healthcare Administrator",
        "Emergency Medical Assistant",
        "Surgical Assistant",
        "Primary Care Assistant",
        "Specialty Clinic Assistant"
      ],
      industries: [
        "Hospitals and Clinics",
        "Primary Care Centers",
        "Emergency Departments",
        "Surgical Centers",
        "Specialty Clinics",
        "Government Health Services",
        "Community Health Centers",
        "Telemedicine Companies",
        "Healthcare Consulting",
        "Medical Research"
      ],
      averageSalary: "₹4-10 LPA (varies by experience and specialization)",
      topRecruiters: [
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Max Healthcare",
        "AIIMS",
        "Manipal Hospitals",
        "Government Hospitals",
        "Primary Health Centers",
        "Emergency Services",
        "Specialty Clinics",
        "Healthcare Startups"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy",
          "Human Physiology",
          "Biochemistry",
          "Medical Terminology",
          "Communication Skills",
          "Computer Applications",
          "Healthcare Systems",
          "Professional Ethics"
        ]
      },
      {
        year: 2,
        subjects: [
          "Pathology",
          "Pharmacology",
          "Microbiology",
          "Clinical Medicine",
          "Diagnostic Procedures",
          "Patient Care",
          "Medical Documentation",
          "Healthcare Management"
        ]
      },
      {
        year: 3,
        subjects: [
          "Clinical Rotations",
          "Specialty Medicine",
          "Emergency Medicine",
          "Surgical Assistance",
          "Community Health",
          "Research Methodology",
          "Internship",
          "Professional Development"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering BPA program",
      "Appear for entrance examination (if required)",
      "Merit-based selection and counseling",
      "Document verification",
      "Medical fitness examination",
      "Fee payment and admission",
      "Orientation program",
      "Begin academic and clinical training"
    ],
    topColleges: [
      {
        name: "Manipal College of Allied Health Sciences",
        location: "Manipal, Karnataka",
        fees: "₹4 LPA",
        ranking: "Leading Private College"
      },
      {
        name: "JSS Academy of Higher Education",
        location: "Mysore, Karnataka",
        fees: "₹3.5 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Symbiosis Institute of Health Sciences",
        location: "Pune, Maharashtra",
        fees: "₹5 LPA",
        ranking: "Private University"
      },
      {
        name: "Apollo Institute of Medical Sciences",
        location: "Hyderabad, Telangana",
        fees: "₹4.5 LPA",
        ranking: "Industry-Linked"
      }
    ],
    skills: [
      "Clinical Assessment",
      "Patient Communication",
      "Medical Procedures",
      "Documentation Skills",
      "Problem Solving",
      "Time Management",
      "Teamwork",
      "Empathy",
      "Technical Skills",
      "Critical Thinking",
      "Adaptability",
      "Professional Ethics"
    ],
    futureScope: "The Physician Assistant profession is rapidly growing globally due to healthcare workforce shortages and increasing healthcare demands. Future opportunities include specialization in areas like emergency medicine, surgery, primary care, and telemedicine. The integration of AI and digital health tools is creating new roles in healthcare delivery and patient management. As healthcare systems evolve, PAs will play increasingly important roles in providing accessible and cost-effective medical care.",
    faqs: [
      {
        question: "What is the scope of practice for Physician Assistants?",
        answer: "Physician Assistants can perform physical examinations, diagnose conditions, develop treatment plans, prescribe medications (where legally permitted), assist in surgeries, and provide patient education under physician supervision."
      },
      {
        question: "Is the Physician Assistant program recognized in India?",
        answer: "The PA profession is emerging in India with some institutions offering programs. However, regulatory frameworks are still being developed, and scope of practice may vary by state and institution."
      },
      {
        question: "Can Physician Assistants work independently?",
        answer: "PAs typically work under physician supervision, though the level of supervision may vary. In some settings, experienced PAs may have considerable autonomy while maintaining collaborative relationships with physicians."
      },
      {
        question: "What are the career advancement opportunities?",
        answer: "Career advancement includes specialization in specific medical areas, leadership roles in healthcare teams, teaching positions, healthcare administration, or pursuing advanced degrees in healthcare management."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {physicianAssistantData.title} ({physicianAssistantData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={physicianAssistantData} />
      </div>
    </div>
  );
};

export default PhysicianAssistantCoursePage;